import axios from 'axios';
import config from '../config';
import { showError } from '../utils';

import { store } from '../store';

// Generic network call function to handle common API request patterns
function networkCall({ method, url, headers, params, data, ...extraOptions }) {
  return axios({
    method,
    url,
    headers,
    params,
    data,
    ...extraOptions,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const error = new Error('Invalid response');
        error.data = responseBody;
        throw error;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function fetchClaimsFilters(policyId, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/claims/filters?policyId=${policyId}`,
    headers,
  });
}

export function fetchClaims({
  authToken,
  relativeId,
  claimId,
  sortBy,
  filter,
  claimType,
  dateRange,
  dateRangeStart,
  dateRangeEnd,
  policyYearStart,
  policyYearEnd,
} = {}) {
  const headers = { authorization: authToken };
  const params = new URLSearchParams();

  const setArray = (key, values) => {
    const list = (Array.isArray(values) ? values : [values]).filter(
      (v) => v !== undefined && v !== null && v !== ''
    );
    if (list.length) params.set(key, JSON.stringify(list));
  };

  if (relativeId) params.set('relativeId', relativeId);
  if (claimId) params.set('claimId', claimId);
  if (sortBy) params.set('sortBy', sortBy);
  if (filter) setArray('filter', filter);
  if (claimType) setArray('claimType', claimType);
  if (dateRange) params.set('dateRange', dateRange);
  if (dateRangeStart) params.set('startDate', dateRangeStart);
  if (dateRangeEnd) params.set('endDate', dateRangeEnd);
  if (policyYearStart) params.set('policyYearStart', policyYearStart);
  if (policyYearEnd) params.set('policyYearEnd', policyYearEnd);

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/claims?${params.toString()}`,
    headers,
  });
}

export function fetchClaimSummary(claimId, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/${claimId}/claim-status`,
    headers,
  });
}

export function fetchClaimStatusSummaryV2(claimId, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/${claimId}/claim-status-details`,
    headers,
  });
}

export function fetchClaimSummaryV2(claimId, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/${claimId}/claim-status`,
    headers,
  });
}

export function uploadClaimsDocuments(body, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/reimbursements/v2/upload-documents`,
    headers,
    data: body,
  });
}

export function getFeedbackDetails(claimId, authToken) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/feedback-details?reimbursementId=${claimId}`,
    headers,
  });
}

export function postFeedbackDetails(formData, authToken) {
  const headers = { authorization: authToken };

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/reimbursements/v2/feedback`,
    headers,
    data: formData,
  });
}

export function knowMoreDetails(claimId, authToken) {
  const headers = { authorization: authToken };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/documents-verified-view-more?reimbursementId=${claimId}`,
    headers,
  });
}

export function fetchGmcDocuments(authToken, claimId) {
  const headers = {
    authorization: authToken,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/gmc/${claimId}/claim-documents`,
    headers,
  });
}

export function submitGmcClaimDocs(authToken, body) {
  const headers = {
    authorization: authToken,
  };
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/gmc/claim-documents`,
    headers,
    data: body,
  });
}

export function submitClaimReview(body, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/reimbursements/v2/review`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function submitGmcClaimReview(body, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/gmc/claims/review`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function getPendingFeedback(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/reimbursements/v2/pending-feedback`,
    headers: { authorization: authToken },
  });
}
